import React, { useEffect, useState } from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
// Framer Motion is already imported at the top of the file:
// import { motion, AnimatePresence } from 'framer-motion';

// Animation variants for smooth transitions
const variants = {
  hidden: { opacity: 0, height: 0, overflow: 'hidden' },
  visible: { opacity: 1, height: 'auto', overflow: 'visible' },
  exit: { opacity: 0, height: 0, overflow: 'hidden' }
};

// Card animation for hover effects
const cardVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.02, transition: { duration: 0.2 } }
};


// .replace(/^'|'$/g, '');

/**
 * Removes single quotes from the beginning and end of a string
 * @param {string} text - The text to format
 * @returns {string} - The formatted text without leading or trailing single quotes
 */
const formatText = (text) => {
    return text;
    // if (!text) return '';
    // return text.trim()
    //     .replace(/^[''']|[''']$/g, '')  // Remove leading/trailing straight or curly apostrophes
    //     .replace(/\s+([,.])/g, '$1')     // Remove spaces before commas or periods
    //     .replace(/([,.])(\S)/g, '$1 $2')  // Ensure exactly one space after commas or periods
    //     .trim();
};



/**
 * Formats the date to a relative time string (e.g. "2 days ago")
 * @param {string} dateString - The date string to format
 * @returns {string} - The formatted relative time
 */
const formatRelativeDate = (timestamp) => {
    // Add validation to prevent invalid date errors
    if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        return 'Invalid date';
    }

    try {
        return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'Date unavailable';
    }
};

const isValidUrl = (urlString) => {
    try {
        return Boolean(urlString && urlString.startsWith('http') && new URL(urlString));
    } catch (e) {
        return false;
    }
};

const CompAIStoryBoardItem = ({ newsItem }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCaseStudiesModalOpen, setIsCaseStudiesModalOpen] = useState(false);



    /**
     * Formats the text of a news item, applying the formatText function to relevant fields
     * @param {Object} newsItem - The news item to format
     * @returns {Object} - A new news item object with formatted text fields
     */
    const formatNewsItem = (newsItem) => {
        if (!newsItem) return {};

        return {
            ...newsItem,
            headline: formatText(newsItem.headline),
            summary: formatText(newsItem.summary),
            relevance: formatText(newsItem.relevance),
            impact: formatText(newsItem.impact),
            sales_takeaways: Array.isArray(newsItem.sales_takeaways)
                ? newsItem.sales_takeaways.map(takeaway => formatText(takeaway))
                : []
        };
    };

    useEffect(() => {
        const formattedNewsItem = formatNewsItem(newsItem);
        //console.log(formattedNewsItem);
    }, [newsItem]);


    return (
        <div className="   rounded-xl overflow-hidden flex flex-col  ">
            <div className="flex flex-col h-full  ">
                {(() => {
                    // Determine URL and source info
                    const url = newsItem.url || `https://www.google.com/search?q=${encodeURIComponent(newsItem.headline || '')}`;
                    const isGoogleSearch = !newsItem.url;
                    const iconSrc = isGoogleSearch
                        ? '/util/app/b3-admin-google.png'
                        : `https://www.google.com/s2/favicons?domain=${isValidUrl(newsItem.url) ? new URL(newsItem.url).hostname : ''}&sz=64`;
                    const sourceName = isGoogleSearch ? 'Google Search' : newsItem.source;

                    return (
                        <div className="mb-8">
                            {/* "Why" statement with premium styling */}


                            {/* Source information card */}
                            <div className="rounded-xl overflow-hidden bg-black/20 ">
                                <div className="p-6">
                                    <div className=" ">

                                        <div className="flex justify-between items-center mb-4">
                                            <div className="text-green-300/80 text-lg font-medium leading-6 pr-3">
                                                {formatText(newsItem.headline)}
                                            </div>
                                            {newsItem.caseStudies && newsItem.caseStudies.length > 0 && (
                                                <>
                                                    <motion.button
                                                        onClick={() => setIsCaseStudiesModalOpen(true)}
                                                        className="flex-shrink-0 bg-black/20  rounded-lg px-3 py-3 hover:bg-black/30 hover:border-zinc-700 transition-colors -mr-2 -mt-2"
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        <div className="text-zinc-200 text-sm font-medium">
                                                            <span className="text-2xl">{newsItem.caseStudies.length}</span>
                                                            <br/> 
                                                            <span className="text-zinc-300/80">{newsItem.caseStudies.length === 1 ? 'case study' : 'case studies'}</span>
                                                        </div>
                                                    </motion.button>
                                                    
                                                    <AnimatePresence>
                                                        {isCaseStudiesModalOpen && (
                                                            <motion.div 
                                                                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                                exit={{ opacity: 0 }}
                                                                onClick={() => setIsCaseStudiesModalOpen(false)}
                                                            >
                                                                <motion.div 
                                                                    className="bg-zinc-900   rounded-xl w-3/4 max-h-[80vh] overflow-y-auto"
                                                                    initial={{ scale: 0.9, y: 20 }}
                                                                    animate={{ scale: 1, y: 0 }}
                                                                    exit={{ scale: 0.9, y: 20 }}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <div className="p-6">
                                                                        <div className="flex justify-between items-center mb-6">
                                                                            <h2 className="text-xl font-normal text-zinc-50 ">Case Studies</h2>
                                                                            <button 
                                                                                onClick={() => setIsCaseStudiesModalOpen(false)}
                                                                                className="text-zinc-400 hover:text-zinc-200"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                                </svg>
                                                                            </button>
                                                                        </div>
                                                                        
                                                                        <div className="  grid grid-cols-2 gap-6">
                                                                            {newsItem.caseStudies.map((caseStudy, index) => (
                                                                                <div key={index} className="bg-black/20  rounded-lg p-8">
                                                                                    <div className="flex justify-between items-start mb-3">
                                                                                        <h3 className="text-xl font-normal text-zinc-50">{caseStudy.name}</h3>
                                                                                        
                                                                                        <span className={`text-xs font-medium px-3 py-2 rounded-full ${
                                                                                            caseStudy.rating === 'high' ? 'bg-green-900/50 text-green-300' : 
                                                                                            caseStudy.rating === 'medium' ? 'bg-yellow-900/50 text-yellow-300' : 
                                                                                            'bg-red-900/50 text-red-300'
                                                                                        }`}>
                                                                                            {caseStudy.rating.charAt(0).toUpperCase() + caseStudy.rating.slice(1)} Relevance
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="text-zinc-200 text-lg">{caseStudy.biggest_takeaway.replace(/\.$/, '')}</div>
                                                                                    <p className="text-zinc-400 text-base mt-4">{caseStudy.details}</p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </motion.div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </>
                                            )}
                                        </div>

                                        <div className="text-zinc-300 text-base leading-6 mb-1">
                                            {formatText(newsItem.why || '')}
                                        </div>
                                        {newsItem.sales_takeaways && newsItem.sales_takeaways.length > 0 && (
                                            <div className="py-6">
                                                <h3 className="text-base   text-green-300   tracking-wider mb-4 flex items-center">

                                                    Solution Takeaways
                                                </h3>
                                                <div className="space-y-4  ">
                                                    {newsItem.sales_takeaways.map((takeaway, i) => (
                                                        <div
                                                            key={i}
                                                            className="flex items-start gap-3 "
                                                        >
                                                            <div className="flex-shrink-0 w-6  h-6 rounded-full bg-black/50 flex items-center justify-center mt-0">
                                                                <span className="text-sm font-bold text-zinc-500">{i + 1}</span>
                                                            </div>
                                                            <div className="text-zinc-300 text-base">
                                                                {formatText(takeaway)}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {/* <div className="h-1 w-20 bg-gradient-to-r from-sky-400 to-sky-500 rounded-full"></div> */}
                                    </div>
                                    <a
                                        href={url}
                                        target="_blank">
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 -mb-2  border border-zinc-900 bg-black/10 hover:bg-black/30 -mx-2 p-2 rounded-lg">
                                            <div className="flex items-center gap-4">
                                                <div className="flex-shrink-0 p-3 rounded-lg bg-black/40 shadow-inner border border-white/5">
                                                    <img
                                                        src={iconSrc}
                                                        alt=""
                                                        className="w-6 h-6"
                                                        onError={(e) => { e.target.style.display = 'none' }}
                                                    />
                                                </div>
                                                <div className="flex flex-col">
                                                    {/* <div className="text-zinc-100 text-base     tracking-tight line-clamp-2">
                                                        {formatText(newsItem.headline)}
                                                    </div> */}
                                                    <div className="text-zinc-400 text-sm mt-1 flex items-center">
                                                        <span>{sourceName}</span>
                                                        {!isGoogleSearch && (
                                                            <span className="mx-2 text-zinc-600">•</span>
                                                        )}
                                                        <span>{newsItem.datePublished ? formatRelativeDate(newsItem.datePublished) : ''}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                rel="noopener noreferrer"
                                                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium transition-all duration-200 "
                                                title="View source"
                                            >
                                                <span> </span>
                                                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                                            </div>
                                        </div>
                                    </a>
                                </div>



                                {/* Toggle button with improved styling */}
                                <div className="flex justify-center -mt-7  relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full "></div>
                                    </div>
                                    <button
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className="relative z-10 flex items-center justify-center    rounded-lg border 
                                         border-zinc-900 bg-black/10  hover:bg-black/30 py-5 text-green-200 text-sm transition-all duration-300 px-4 my-4 w-full mx-4"
                                        aria-label={isExpanded ? "Hide details" : "Show details"}
                                    > 
                                        Why Change, Why Us {isExpanded ? (
                                            <MinusIcon className="w-5 h-5 ml-3 text-green-200" />
                                        ) : (
                                            <PlusIcon className="w-5 h-5 ml-3 text-green-200" />
                                        )}
                                        
                                    </button>
                                </div>

                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                                            className="overflow-hidden"
                                        >
                                            <div className="rounded-xl  pb-6   px-6 space-y-8">
                                                {/* Summary Section */}
                                                {newsItem.summary && (
                                                    <div>
                                                        <h3 className="text-base   text-green-200   tracking-wider mb-3 flex items-center">
                                                            {/* <div className="w-2 h-2 bg-zinc-400 rounded-full mr-2"></div> */}
                                                            Summary
                                                        </h3>
                                                        <div className="text-zinc-300 text-base leading-relaxed">
                                                            {formatText(newsItem.summary)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Relevance Section (Why Change) */}
                                                {newsItem.relevance && (
                                                    <div>
                                                        <div className="relative   mb-1">
                                                            {/* <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-sky-400 to-sky-600 rounded-full"></div> */}
                                                            <h3 className="text-base   text-green-200   tracking-wider mb-3 flex items-center">
                                                                Why Change
                                                            </h3>
                                                        </div>
                                                        <div className="text-zinc-300 text-base leading-relaxed">
                                                            {formatText(newsItem.relevance)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Impact Section (Why Us) */}
                                                {newsItem.impact && (
                                                    <div className="">
                                                        <div className="relative   mb-1">
                                                            {/* <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-sky-400 to-sky-600 rounded-full"></div> */}
                                                            <h3 className="text-base   text-green-200   tracking-wider mb-3 flex items-center">
                                                                Why Us
                                                            </h3>
                                                        </div>
                                                        <div className="text-zinc-300 text-base leading-relaxed">
                                                            {formatText(newsItem.impact)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Sales Takeaways with premium design */}

                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>



                        </div>
                    );
                })()}

            </div>
        </div>
    );
};

export default CompAIStoryBoardItem; 